import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    createMemoryState,
    GameResultOverlay,
    GameShell,
} from '../shared';
import { formatGameCopy } from './gameCopy';

const MEMORY_MATCH_ASSET_SET_ID = 'dogs';

const getDogIdentity = (card, copy) => {
    const parsedNumber = Number.parseInt(`${card.matchId || ''}`.replace(/\D/g, ''), 10);
    const number = Number.isFinite(parsedNumber) ? parsedNumber : card.matchId;
    return formatGameCopy(copy.memoryDogIdentity, { number });
};

const getMemoryCardLabel = ({ card, copy, index, matched, total, visible }) => {
    if (!visible) {
        return formatGameCopy(copy.memoryHiddenCard, {
            position: index + 1,
            total,
        });
    }

    const dog = getDogIdentity(card, copy);
    return formatGameCopy(
        matched
            ? copy.memoryMatchedCard
            : copy.memoryRevealedCard,
        { dog }
    );
};

function MemoryMatchGame({
    copy = {},
    initialLevel = 5,
    onRunComplete = null,
    pairCount = null,
    showResults = true,
    ...props
}) {
    const hasRequestedPairCount = pairCount !== null && pairCount !== undefined && pairCount !== '';
    const requestedPairCount = Number(pairCount);
    const requestedLevel = Number(initialLevel);
    const level = Math.max(1, Math.min(5, Math.round(
        hasRequestedPairCount && Number.isFinite(requestedPairCount)
            ? requestedPairCount - 3
            : (Number.isFinite(requestedLevel) ? requestedLevel : 5)
    )));
    const effectivePairCount = hasRequestedPairCount && Number.isFinite(requestedPairCount)
        ? Math.max(1, Math.min(8, Math.round(requestedPairCount)))
        : level + 3;
    const completionReportedRef = useRef(false);
    const [memoryState, setMemoryState] = useState(() => (
        createMemoryState('', effectivePairCount, MEMORY_MATCH_ASSET_SET_ID)
    ));
    const [flipped, setFlipped] = useState([]);
    const [matched, setMatched] = useState([]);
    const [pairFeedback, setPairFeedback] = useState({ indexes: [], type: '' });
    const [message, setMessage] = useState('ready');
    const { deck } = memoryState;
    const matchedSet = useMemo(() => new Set(matched), [matched]);
    const won = matched.length === deck.length;

    useEffect(() => {
        if (flipped.length !== 2) {
            return undefined;
        }

        const [firstIndex, secondIndex] = flipped;
        const firstCard = deck[firstIndex];
        const secondCard = deck[secondIndex];
        const matching = firstCard.matchId === secondCard.matchId;
        setMessage(matching ? 'good' : 'miss');
        setPairFeedback({ indexes: [firstIndex, secondIndex], type: matching ? 'correct' : 'wrong' });

        const timer = window.setTimeout(() => {
            if (matching) {
                setMatched((current) => {
                    const next = [...current, firstCard.id, secondCard.id];
                    setMessage(next.length === deck.length ? 'done' : 'good');
                    return next;
                });
            }
            setPairFeedback({ indexes: [], type: '' });
            setFlipped([]);
        }, matching ? 480 : 900);

        return () => window.clearTimeout(timer);
    }, [deck, flipped]);

    useEffect(() => {
        if (!won || completionReportedRef.current) {
            return;
        }

        completionReportedRef.current = true;
        onRunComplete?.({ level, maxScore: deck.length / 2, score: deck.length / 2 });
    }, [deck.length, level, onRunComplete, won]);

    const reset = () => {
        completionReportedRef.current = false;
        setMemoryState(createMemoryState('', effectivePairCount, MEMORY_MATCH_ASSET_SET_ID));
        setFlipped([]);
        setMatched([]);
        setPairFeedback({ indexes: [], type: '' });
        setMessage('ready');
    };

    const flipCard = (index) => {
        const card = deck[index];

        if (won || matchedSet.has(card.id) || flipped.includes(index) || flipped.length >= 2) {
            return;
        }

        if (flipped.length === 0) {
            setMessage('ready');
            setPairFeedback({ indexes: [], type: '' });
        }

        setFlipped((current) => {
            const next = [...current, index];
            return next;
        });
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level, maxScore: deck.length / 2, score: Math.floor(matched.length / 2) }}
            tone={message}
        >
            <div className="UFOV-brain-games__memory-grid">
                {deck.map((card, index) => {
                    const visible = flipped.includes(index) || matchedSet.has(card.id);
                    const matchedCard = matchedSet.has(card.id);
                    const feedbackType = pairFeedback.indexes.includes(index) ? pairFeedback.type : '';
                    const cardDisabled = won || matchedCard || visible || flipped.length >= 2;
                    return (
                        <button
                            aria-label={getMemoryCardLabel({
                                card,
                                copy,
                                index,
                                matched: matchedCard,
                                total: deck.length,
                                visible,
                            })}
                            aria-pressed={visible}
                            className={`UFOV-brain-games__memory-card ${visible ? 'is-open' : ''} ${matchedCard ? 'is-matched' : ''} ${feedbackType ? `is-${feedbackType}` : ''}`.trim()}
                            disabled={cardDisabled}
                            key={card.id}
                            type="button"
                            onClick={() => flipCard(index)}
                        >
                            <span className="UFOV-brain-games__memory-card-inner" aria-hidden="true">
                                <span className="UFOV-brain-games__memory-card-face is-back">
                                    <img
                                        alt=""
                                        aria-hidden="true"
                                        className="UFOV-brain-games__memory-card-back-logo"
                                        draggable="false"
                                        src="/assets/ufov/sopt-mark-white.svg"
                                    />
                                </span>
                                <span className="UFOV-brain-games__memory-card-face is-front">
                                    <img alt="" draggable="false" src={card.src} />
                                </span>
                            </span>
                        </button>
                    );
                })}
            </div>
            {won && showResults ? (
                <GameResultOverlay
                    metrics={{ level, maxScore: deck.length / 2, score: deck.length / 2 }}
                    onRestart={reset}
                />
            ) : null}
        </GameShell>
    );
}

export default MemoryMatchGame;
